import AutoNumeric from "autonumeric";
import * as FilePond from "filepond";

import { apiBaseUrl } from "./api.js";
import {
	FATURA_CURRENCY_OPTIONS,
	buildFaturaKdvOptions,
	calculateFaturaTotals,
	formatFaturaAmount,
	getFaturaCurrencySymbol,
	normalizeFaturaQuantityInputValue,
	normalizeFaturaCurrency,
	parseFaturaQuantity,
} from "../faturaCurrency.js";
import { getTodayLocalDateInputValue } from "../../../../utils/dateOnly.js";
import { bindAmountPasteGuard } from "../../amountPasteGuard.js";
import {
	populateAmortismanSelect,
	updateAmortismanPreview,
} from "../../../amortisman/amortismanUtils.js";

const numberFormatter = new Intl.NumberFormat("tr-TR", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export const faturaInstances = new Map();
let amortismanDefinitions = [];

function findFaturaField(cardEl, fieldName) {
	return cardEl?.querySelector(`[data-fatura-field="${fieldName}"]`) ?? null;
}

function nOrNull(anInstance) {
	if (!anInstance) return null;
	const raw = anInstance.getNumericString();
	if (raw === null || raw === undefined || raw === "") return null;
	const value = Number(raw);
	return Number.isFinite(value) ? value : null;
}

function isDraftUploadPath(filePath) {
	return typeof filePath === "string" && filePath.startsWith("/uploads/_drafts/");
}

function buildCurrencyOptions(selected = "TRY") {
	const normalized = normalizeFaturaCurrency(selected);
	return FATURA_CURRENCY_OPTIONS.map(
		(option) =>
			`<option value="${option.code}"${option.code === normalized ? " selected" : ""}>${option.symbol}</option>`,
	).join("");
}

function revertDraftFile(serverId) {
	if (!isDraftUploadPath(serverId)) return Promise.resolve();
	return fetch(`${apiBaseUrl}/upload/revert`, {
		method: "DELETE",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ serverId }),
	}).catch((err) => {
		console.error("draft revert error:", err);
	});
}

function setUploadState(cardEl, state) {
	const fieldEl = cardEl.querySelector(".column-container.create-gorsel.create-fatura");
	if (!fieldEl) return;
	fieldEl.classList.remove("is-valid", "is-invalid");
	if (state) fieldEl.classList.add(state);
}

function notifyDraftUploadChange(cardEl) {
	const dialog = cardEl.closest("#createDialog");
	if (!dialog) return;
	dialog.dispatchEvent(new CustomEvent("draft-upload-change"));
}

function updateInfoCardFor(faturaId) {
	const inst = faturaInstances.get(faturaId);
	if (!inst) return;

	const { root, anFiyat, quantityEl, kdvEl, currencyEl } = inst;
	const kdvTutarOut = root.querySelector(".fatura-kdv-deger");
	const toplamOut = root.querySelector(".fatura-toplam-deger");
	if (!kdvTutarOut || !toplamOut) return;

	const birimFiyat = nOrNull(anFiyat);
	const rawBirimMiktar = String(quantityEl?.value ?? "").trim();
	const kdvOran = kdvEl?.value ? Number(kdvEl.value) : null;
	const currencyCode = normalizeFaturaCurrency(currencyEl?.value);
	const totals = calculateFaturaTotals({
		birimFiyat,
		birimMiktar: quantityEl?.value,
		kdvOrani: kdvOran,
	});

	if (birimFiyat === null || (rawBirimMiktar !== "" && parseFaturaQuantity(rawBirimMiktar) === null)) {
		kdvTutarOut.textContent = "—";
		toplamOut.textContent = "—";
		kdvTutarOut.title = "—";
		toplamOut.title = "—";
		return;
	}

	const kdvText = formatFaturaAmount(numberFormatter, totals.kdvTutar, currencyCode);
	const toplamText = formatFaturaAmount(numberFormatter, totals.satinAlmaDegeri, currencyCode);

	kdvTutarOut.textContent = kdvText;
	toplamOut.textContent = toplamText;
	kdvTutarOut.title = kdvText;
	toplamOut.title = toplamText;
}

function destroyOneFaturaCard(cardEl) {
	const faturaId = cardEl.dataset.faturaId;
	if (!faturaId) return;

	const inst = faturaInstances.get(faturaId);
	if (inst?.pond) {
		inst.pond.getFiles().forEach((file) => {
			revertDraftFile(file.serverId);
		});
		inst.pond.destroy();
	}

	if (inst?.anFiyat) {
		try {
			inst.anFiyat.remove();
		} catch {
			// ignore
		}
	}

	const hidden = cardEl.querySelector(".fatura-pdf-path");
	if (hidden) hidden.value = "";

	faturaInstances.delete(faturaId);
	notifyDraftUploadChange(cardEl);
}

function bindDateValidation(dateInput) {
	if (!dateInput || dateInput._boundFuture) return;
	dateInput._boundFuture = true;
	dateInput.max = getTodayLocalDateInputValue();

	const validate = () => {
		if (!dateInput.value) {
			dateInput.setCustomValidity("");
			return;
		}

		const today = getTodayLocalDateInputValue();
		dateInput.setCustomValidity(
			dateInput.value > today ? "Gelecekte bir tarih secemezsiniz." : "",
		);
	};

	dateInput.addEventListener("change", validate);
	dateInput.addEventListener("blur", validate);
}

function bindQuantityValidation(quantityInput, onChange) {
	if (!quantityInput || quantityInput.dataset.boundQuantity === "true") return;
	quantityInput.dataset.boundQuantity = "true";

	const errorMessage = "Temsil adedi en az 1 olan tam sayi olmalidir.";
	const emitChange = () => {
		if (typeof onChange === "function") onChange();
	};

	quantityInput.addEventListener("input", () => {
		quantityInput.setCustomValidity("");
		emitChange();
	});

	quantityInput.addEventListener("blur", () => {
		const rawValue = quantityInput.value.trim();
		if (!rawValue) {
			quantityInput.value = normalizeFaturaQuantityInputValue("", 1);
			quantityInput.setCustomValidity("");
			emitChange();
			return;
		}

		const parsed = parseFaturaQuantity(rawValue);
		if (parsed === null) {
			quantityInput.setCustomValidity(errorMessage);
			quantityInput.reportValidity();
			emitChange();
			return;
		}

		quantityInput.value = normalizeFaturaQuantityInputValue(parsed, 1);
		quantityInput.setCustomValidity("");
		emitChange();
	});
}

function initOneFaturaCard(cardEl) {
	const faturaId = cardEl.dataset.faturaId;
	if (!faturaId || faturaInstances.has(faturaId)) return;

	const pdfInput = cardEl.querySelector('input[type="file"].filepond.fatura');
	const pdfPathHidden = cardEl.querySelector(".fatura-pdf-path");
	const tarihEl = findFaturaField(cardEl, "satin_alma_tarih");
	const fiyatEl = findFaturaField(cardEl, "birim_fiyat");
	const quantityEl = findFaturaField(cardEl, "birim_miktar");
	const kdvEl = findFaturaField(cardEl, "kdv_oran");
	const currencyEl = findFaturaField(cardEl, "para_birimi");
	const amortismanEl = findFaturaField(cardEl, "amortisman");
	populateAmortismanSelect(amortismanEl, amortismanDefinitions);

	let pond = null;
	if (pdfInput) {
		pond = FilePond.create(pdfInput, {
			required: true,
			checkValidity: true,
			allowMultiple: false,
			maxFiles: 1,
			maxFileSize: "10MB",
			acceptedFileTypes: ["application/pdf"],
			server: {
				process: {
					url: `${apiBaseUrl}/upload/fatura`,
					method: "POST",
					onload: (response) => {
						try {
							const data = JSON.parse(response);
							const filePath = data.filePath || data.path || "";
							return data.serverId || filePath;
						} catch {
							return response;
						}
					},
					onerror: (response) => {
						try {
							const data = JSON.parse(response);
							return data.error || "Upload basarisiz";
						} catch {
							return response || "Upload basarisiz";
						}
					},
				},
				revert: (serverId, load, error) => {
					fetch(`${apiBaseUrl}/upload/revert`, {
						method: "DELETE",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ serverId }),
					})
						.then((response) => {
							if (!response.ok) throw new Error(`HTTP ${response.status}`);
							if (pdfPathHidden) pdfPathHidden.value = "";
							load();
						})
						.catch((err) => error(err.message));
				},
			},
		});

		setUploadState(cardEl, "is-invalid");

		pond.on("updatefiles", (files) => {
			setUploadState(cardEl, files.length ? "is-valid" : "is-invalid");
		});

		pond.on("processfile", (err, file) => {
			if (err) return;
			if (pdfPathHidden) pdfPathHidden.value = file?.serverId || "";
			setUploadState(cardEl, "is-valid");
			notifyDraftUploadChange(cardEl);
		});

		pond.on("removefile", () => {
			if (pdfPathHidden) pdfPathHidden.value = "";
			setUploadState(cardEl, "is-invalid");
			notifyDraftUploadChange(cardEl);
		});

		pond.on("processfilerevert", () => {
			if (pdfPathHidden) pdfPathHidden.value = "";
			setUploadState(cardEl, "is-invalid");
			notifyDraftUploadChange(cardEl);
		});
	}

	bindDateValidation(tarihEl);
	if (tarihEl && !tarihEl.dataset.amortismanPreviewBound) {
		tarihEl.dataset.amortismanPreviewBound = "true";
		tarihEl.addEventListener("change", () => updateAmortismanPreview(cardEl));
	}
	if (amortismanEl && !amortismanEl.dataset.previewBound) {
		amortismanEl.dataset.previewBound = "true";
		amortismanEl.addEventListener("change", () => updateAmortismanPreview(cardEl));
	}
	bindQuantityValidation(quantityEl, () => updateInfoCardFor(faturaId));

	let anFiyat = null;
	if (fiyatEl && !fiyatEl._an) {
		anFiyat = new AutoNumeric(fiyatEl, {
			currencySymbol: ` ${getFaturaCurrencySymbol(currencyEl?.value)}`,
			currencySymbolPlacement: "s",
			decimalCharacter: ",",
			digitGroupSeparator: ".",
			decimalPlaces: 2,
			minimumValue: "0",
			maximumValue: "999999999",
			modifyValueOnWheel: false,
			emptyInputBehavior: "null",
		});
		fiyatEl._an = anFiyat;
		bindAmountPasteGuard(fiyatEl);

		fiyatEl.addEventListener("blur", () => {
			if (anFiyat.getNumericString() === null) fiyatEl.value = "";
		});
		fiyatEl.addEventListener("input", () => updateInfoCardFor(faturaId));
	} else if (fiyatEl?._an) {
		anFiyat = fiyatEl._an;
	}
	bindAmountPasteGuard(fiyatEl);

	if (kdvEl) {
		kdvEl.addEventListener("change", () => updateInfoCardFor(faturaId));
	}

	if (currencyEl && !currencyEl.dataset.bound) {
		currencyEl.dataset.bound = "true";
		currencyEl.addEventListener("change", () => {
			const symbol = getFaturaCurrencySymbol(currencyEl.value);
			anFiyat?.update({ currencySymbol: ` ${symbol}` });
			updateInfoCardFor(faturaId);
		});
	}

	faturaInstances.set(faturaId, {
		root: cardEl,
		pond,
		anFiyat,
		quantityEl,
		kdvEl,
		currencyEl,
		hasFile: false,
	});

	const inst = faturaInstances.get(faturaId);
	if (pond) {
		pond.on("addfile", () => {
			inst.hasFile = true;
		});
		pond.on("removefile", () => {
			inst.hasFile = false;
		});
	}

	updateInfoCardFor(faturaId);
	updateAmortismanPreview(cardEl);
}

function renumberFaturas(listEl) {
	const cards = [...listEl.querySelectorAll(".fatura-container")];
	cards.forEach((card, idx) => {
		const indexEl = card.querySelector(".fatura-index");
		if (indexEl) indexEl.textContent = `#${idx + 1}`;
	});
}

function buildFaturaTemplateHtml() {
	return `
    <div class="fatura-container-header">
      <h5>Fatura</h5>
      <h5 class="fatura-index">#1</h5>
      <button class="delete-button" type="button" aria-label="Faturayi sil">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>

    <div class="fatura-container-content">
      <div class="column-container field">
        <label>Fatura Numarasi:</label>
        <input required data-fatura-field="fatura_no" class="form-input" type="text" placeholder="Fatura no" maxlength="150" />
        <span class="info" data-invalid="Gecerli bir fatura numarasi giriniz."></span>
      </div>

      <div class="column-container field">
        <label>Satın Alma Tarihi:</label>
        <input data-fatura-field="satin_alma_tarih" type="date" class="form-input" required />
        <span class="info" data-invalid="Gecerli bir satin alma tarihi giriniz."></span>
      </div>

	  <div class="column-container field">
		<label>Faydal&#305; &#214;m&#252;r:</label>
		<select data-fatura-field="amortisman" class="form-select" required>
		  <option value="">Faydal&#305; &#246;m&#252;r se&#231;iniz</option>
		</select>
		<span class="info" data-focus="Faydal&#305; &#246;m&#252;r tan&#305;m&#305; veya Amortisman uygulanmaz se&#231;iniz." data-invalid="Faydal&#305; &#246;m&#252;r se&#231;imi zorunludur."></span>
	  </div>

	  <div class="info-card-yeni fatura-amortisman-preview">
		<div class="info-card-yeni-satir">
		  <div class="info-card-yeni-etiket">Faydal&#305; &#214;m&#252;r</div>
		  <div class="info-card-yeni-deger fatura-amortisman-yil">&#8212;</div>
		</div>
		<div class="info-card-yeni-satir">
		  <div class="info-card-yeni-etiket">Tahmini Biti&#351;</div>
		  <div class="info-card-yeni-deger fatura-amortisman-bitis">&#8212;</div>
		</div>
	  </div>

      <div class="column-container field birim-fiyat">
        <label>Birim Fiyat:</label>
        <div class="currency-input-wrap">
          <input data-fatura-field="birim_fiyat" class="form-input currency-input" type="text" placeholder="Birim fiyat" required />
          <select data-fatura-field="para_birimi" class="currency-select" aria-label="Para birimi">
            ${buildCurrencyOptions("TRY")}
          </select>
        </div>
        <span class="info" data-invalid="Gecerli bir birim fiyat giriniz."></span>
      </div>

      <div class="column-container field">
        <label>Temsil Adedi:</label>
        <input data-fatura-field="birim_miktar" class="form-input" type="text" inputmode="numeric" value="1" placeholder="Temsil adedi" required />
        <span class="info" data-focus="Bu demirbas kaydi faturada kac adet urunu temsil ediyor." data-invalid="Temsil adedi en az 1 olan tam sayi olmalidir."></span>
      </div>

      <div class="column-container field kdv-oran">
        <label>KDV Orani:</label>
        <select data-fatura-field="kdv_oran" class="form-select" required>
          <option value="" selected>KDV orani seciniz</option>
          ${buildFaturaKdvOptions()}
        </select>
        <span class="info" data-focus="KDV orani seciniz." data-invalid="Gecerli bir KDV orani seciniz."></span>
      </div>

      <div class="info-card-yeni info-card-yeni--financial">
        <div class="info-card-yeni-satir">
          <div class="info-card-yeni-etiket">KDV Tutar</div>
          <div class="info-card-yeni-deger info-card-yeni-deger--mono fatura-kdv-deger">—</div>
        </div>
        <div class="info-card-yeni-satir">
          <div class="info-card-yeni-etiket">Satin Alma Degeri</div>
          <div class="info-card-yeni-deger info-card-yeni-deger--mono fatura-toplam-deger">—</div>
        </div>
      </div>

      <div class="column-container create-gorsel create-fatura">
        <label>Fatura PDF</label>
        <div class="gorsel-container">
          <input type="file" class="filepond fatura" />
          <input type="hidden" class="fatura-pdf-path" value="" />
        </div>
        <div class="draft-upload-note">
          <span class="material-icons">info</span>
          <span>Yüklenen dosyalar önce taslak olarak tutulur. Kaydetmezseniz 15 dakika sonra otomatik silinir.</span>
        </div>
      </div>

      <div class="column-container field create-fatura-aciklama">
        <label>Fatura Aciklama:</label>
        <textarea
          data-fatura-field="fatura_aciklama"
          class="form-textarea"
          placeholder="Fatura ile ilgili not (opsiyonel)"
          maxlength="500"
        ></textarea>
        <span
          class="info"
          data-focus="Maksimum 500 karakter."
          data-invalid="Aciklama 500 karakteri gecemez."
        ></span>
      </div>
    </div>
  `;
}

function createFaturaCardDom({ faturaId }) {
	const card = document.createElement("div");
	card.className = "fatura-container";
	card.dataset.faturaId = String(faturaId);
	card.innerHTML = buildFaturaTemplateHtml();
	return card;
}

export function initFaturaSection(createDialogEl) {
	if (!createDialogEl) return;

	const faturaList = createDialogEl.querySelector("#faturaList");
	if (!faturaList) return;

	const addBtn = createDialogEl.querySelector(".add-new-fatura .btn-add");
	if (!addBtn) return;

	if (faturaList.querySelectorAll(".fatura-container").length === 0) {
		const first = createFaturaCardDom({ faturaId: 1 });
		faturaList.appendChild(first);
		initOneFaturaCard(first);
		renumberFaturas(faturaList);
	}

	if (!addBtn.dataset.bound) {
		addBtn.dataset.bound = "true";
		addBtn.addEventListener("click", () => {
			const ids = [...faturaList.querySelectorAll(".fatura-container")]
				.map((card) => Number(card.dataset.faturaId || 0))
				.filter((value) => Number.isFinite(value));

			const nextId = (ids.length ? Math.max(...ids) : 0) + 1;
			const card = createFaturaCardDom({ faturaId: nextId });
			faturaList.appendChild(card);
			initOneFaturaCard(card);
			renumberFaturas(faturaList);
		});
	}

	if (!faturaList.dataset.bound) {
		faturaList.dataset.bound = "true";
		faturaList.addEventListener("click", (event) => {
			const delBtn = event.target.closest(".delete-button");
			if (!delBtn) return;

			const card = delBtn.closest(".fatura-container");
			if (!card) return;

			const all = [...faturaList.querySelectorAll(".fatura-container")];
			if (all.length <= 1) {
				alert("En az 1 fatura olmali. Ilk faturayi kaldiramazsiniz.");
				return;
			}

			destroyOneFaturaCard(card);
			card.remove();
			renumberFaturas(faturaList);
		});
	}
}

export function setFaturaAmortismanDefinitions(createDialogEl, definitions) {
	amortismanDefinitions = Array.isArray(definitions) ? definitions : [];
	createDialogEl?.querySelectorAll(".fatura-container").forEach((cardEl) => {
		populateAmortismanSelect(findFaturaField(cardEl, "amortisman"), amortismanDefinitions);
		updateAmortismanPreview(cardEl);
	});
}

export function resetFaturaSection(createDialogEl) {
	const faturaList = createDialogEl?.querySelector("#faturaList");

	faturaInstances.forEach((inst, id) => {
		if (id === "1") return;
		destroyOneFaturaCard(inst.root);
		inst.root.remove();
	});

	const firstInst = faturaInstances.get("1");
	if (firstInst) {
		firstInst.pond?.removeFiles();
		firstInst.anFiyat?.clear();
		if (firstInst.kdvEl) firstInst.kdvEl.value = "";
		if (firstInst.quantityEl) {
			firstInst.quantityEl.value = "1";
			firstInst.quantityEl.setCustomValidity("");
		}
		if (firstInst.currencyEl) firstInst.currencyEl.value = "TRY";
		firstInst.anFiyat?.update({
			currencySymbol: ` ${getFaturaCurrencySymbol("TRY")}`,
		});
		const firstCard = firstInst.root;
		findFaturaField(firstCard, "fatura_no")?.setAttribute("value", "");
		const faturaNo = findFaturaField(firstCard, "fatura_no");
		if (faturaNo) faturaNo.value = "";
		const tarih = findFaturaField(firstCard, "satin_alma_tarih");
		if (tarih) tarih.value = "";
		const aciklama = findFaturaField(firstCard, "fatura_aciklama");
		if (aciklama) aciklama.value = "";
		const hidden = firstCard.querySelector(".fatura-pdf-path");
		if (hidden) hidden.value = "";
		const amortisman = findFaturaField(firstCard, "amortisman");
		if (amortisman) amortisman.value = "";
		setUploadState(firstCard, "is-invalid");
		updateInfoCardFor("1");
		updateAmortismanPreview(firstCard);
	}

	if (faturaList) renumberFaturas(faturaList);
}
